class FeaturedCollectionProgress {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector('[data-featured-collection-slider]');
    this.track = section.querySelector('[data-featured-collection-progress]');
    this.thumb = section.querySelector('[data-featured-collection-progress-thumb]');
    this.prevBtn = section.querySelector('[data-featured-collection-prev]');
    this.nextBtn = section.querySelector('[data-featured-collection-next]');
    this.frame = null;

    if (!this.slider) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.update = this.update.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);

    this.slider.addEventListener('scroll', this.handleScroll, { passive: true });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.onPrevClick);
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this.onNextClick);
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.slider);
      if (this.track) this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.update);
    }

    requestAnimationFrame(this.update);
  }

  onPrevClick(e) {
    e.preventDefault();
    this.scrollStep(-1);
  }

  onNextClick(e) {
    e.preventDefault();
    this.scrollStep(1);
  }

  scrollStep(direction) {
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    if (maxScroll <= 0) return;

    const items = Array.from(this.slider.querySelectorAll('.featured-collection-design__item'));
    const sliderRect = this.slider.getBoundingClientRect();
    const currentScroll = this.slider.scrollLeft;

    if (direction === 1) {
      const nextItem = items.find((item) => {
        const itemRect = item.getBoundingClientRect();
        return itemRect.left - sliderRect.left > 4;
      });

      if (nextItem) {
        const itemRect = nextItem.getBoundingClientRect();
        const target = currentScroll + (itemRect.left - sliderRect.left);
        this.slider.scrollTo({ left: Math.min(target, maxScroll), behavior: 'smooth' });
      } else {
        this.slider.scrollTo({ left: maxScroll, behavior: 'smooth' });
      }
    } else {
      const prevItems = items.filter((item) => {
        const itemRect = item.getBoundingClientRect();
        return itemRect.left - sliderRect.left < -4;
      });

      if (prevItems.length > 0) {
        const prevItem = prevItems[prevItems.length - 1];
        const itemRect = prevItem.getBoundingClientRect();
        const target = currentScroll + (itemRect.left - sliderRect.left);
        this.slider.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      } else {
        this.slider.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }
  }

  handleScroll() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.update);
  }

  update() {
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const hasOverflow = maxScroll > 1;

    this.section.classList.toggle('featured-collection-design--no-overflow', !hasOverflow);

    if (this.thumb && this.track) {
      if (!hasOverflow) {
        this.thumb.style.transform = 'translate3d(0, 0, 0)';
      } else {
        const currentScroll = Math.max(0, Math.min(this.slider.scrollLeft, maxScroll));
        const progress = currentScroll / maxScroll;
        const travel = Math.max(this.track.clientWidth - this.thumb.offsetWidth, 0);
        this.thumb.style.transform = `translate3d(${progress * travel}px, 0, 0)`;
      }
    }

    if (this.prevBtn) {
      const isStart = !hasOverflow || this.slider.scrollLeft <= 2;
      this.prevBtn.disabled = isStart;
      this.prevBtn.setAttribute('aria-disabled', isStart ? 'true' : 'false');
    }

    if (this.nextBtn) {
      const isEnd = !hasOverflow || this.slider.scrollLeft >= maxScroll - 2;
      this.nextBtn.disabled = isEnd;
      this.nextBtn.setAttribute('aria-disabled', isEnd ? 'true' : 'false');
    }
  }

  destroy() {
    this.slider?.removeEventListener('scroll', this.handleScroll);
    this.prevBtn?.removeEventListener('click', this.onPrevClick);
    this.nextBtn?.removeEventListener('click', this.onNextClick);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.update);
    if (this.frame) cancelAnimationFrame(this.frame);
  }
}

function initializeFeaturedCollections(root = document) {
  const sections = root.matches?.('[data-featured-collection-section]')
    ? [root]
    : root.querySelectorAll('[data-featured-collection-section]');

  sections.forEach((section) => {
    if (section.dataset.featuredCollectionInitialized === 'true') return;

    section.dataset.featuredCollectionInitialized = 'true';
    section.featuredCollectionProgress = new FeaturedCollectionProgress(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeFeaturedCollections());
} else {
  initializeFeaturedCollections();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeFeaturedCollections(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.matches?.('[data-featured-collection-section]')
    ? event.target
    : event.target.querySelector?.('[data-featured-collection-section]');

  section?.featuredCollectionProgress?.destroy();
});
